import React from 'react'
import { Spin } from 'antd'
import styled, { keyframes, css } from 'styled-components'
import {
  CheckCircleFilled,
  ReloadOutlined,
  VideoCameraOutlined,
  CodeOutlined,
  FormOutlined,
  UnorderedListOutlined,
  InboxOutlined,
} from '@ant-design/icons'

/* ──────────── Theme Tokens ──────────── */
const T = {
  primary: '#3249D7',
  purple: '#8246BE',
  orange: '#FFA642',
  green: '#4cb187',
  green50: '#93D7AF',
  green80: '#DFF0EA',
  red: '#DC3232',
  textPrimary: '#242E39',
  textSecondary: '#637487',
  border: '#EBECEE',
  grey92: '#F0F2F4',
  grey95: '#F2F3F5',
  grey98: '#FBFCFC',
  white: '#ffffff',
  radius: '8px',
  shadowSm: '0px 1px 2px rgba(16, 24, 40, 0.05)',
}

const SECTION_META = {
  mcq: { color: T.primary, icon: <UnorderedListOutlined />, emoji: '' },
  subjective: { color: T.purple, icon: <FormOutlined />, emoji: '' },
  video: { color: T.orange, icon: <VideoCameraOutlined />, emoji: '' },
  coding: { color: T.green, icon: <CodeOutlined />, emoji: '' },
}

/* ──────────── Animations ──────────── */
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ──────────── Styled Components ──────────── */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SectionBlock = styled.div`
  animation: ${fadeIn} 0.25s ease;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.$accentColor || T.border};
`

const SectionIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${props => props.$color}12;
  color: ${props => props.$color};
  font-size: 14px;
`

const SectionTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${T.textPrimary};
  flex: 1;
`

const SectionCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$color || T.textSecondary};
  background: ${props => props.$color ? `${props.$color}14` : T.grey92};
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`

const QuestionCard = styled.div`
  background: ${T.white};
  border: 1px solid ${T.grey95};
  border-left: 3px solid ${props => props.$accent || T.primary};
  border-radius: ${T.radius};
  padding: 16px 18px;
  margin-bottom: 12px;
  box-shadow: ${T.shadowSm};
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  animation: ${fadeIn} 0.2s ease;

  &:hover {
    box-shadow: 0px 2px 8px rgba(16, 24, 40, 0.08);
    border-color: ${T.border};
    border-left-color: ${props => props.$accent || T.primary};
  }
`

const QHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

const QHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const QNumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: ${T.white};
  background: ${props => props.$color || T.primary};
  padding: 2px 10px;
  border-radius: 12px;
  letter-spacing: 0.5px;
  min-width: 32px;
`

const DifficultyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  color: ${props => props.$color};
  background: ${props => props.$bg};
`

const VideoIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${T.orange};
  font-weight: 500;
`

const RegenerateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 500;
  color: ${T.primary};
  background: ${T.white};
  border: 1px solid ${T.border};
  border-radius: ${T.radius};
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;

  &:hover:not(:disabled) {
    background: ${T.primary}08;
    border-color: ${T.primary};
    box-shadow: ${T.shadowSm};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .anticon-reload {
    font-size: 12px;
  }

  ${props => props.$loading && css`
    .anticon-reload {
      animation: ${spin} 1s linear infinite;
    }
  `}
`

const QText = styled.div`
  font-size: 14px;
  color: ${T.textPrimary};
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`

const ScenarioCallout = styled.div`
  background: ${T.grey98};
  border-left: 3px solid ${T.purple}80;
  border-radius: 0 ${T.radius} ${T.radius} 0;
  padding: 12px 16px;
  margin-bottom: 10px;
  font-size: 13px;
  color: ${T.textSecondary};
  line-height: 1.6;
  white-space: pre-wrap;
`

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
`

const OptionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  padding: 10px 14px;
  border-radius: ${T.radius};
  background: ${props => props.$isCorrect ? T.green80 : T.grey98};
  border: 1px solid ${props => props.$isCorrect ? T.green50 : T.grey95};
  color: ${props => props.$isCorrect ? '#065F46' : T.textPrimary};
  font-weight: ${props => props.$isCorrect ? '500' : '400'};
  transition: background 0.15s ease;
  line-height: 1.5;
`

const OptionLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  background: ${props => props.$isCorrect ? T.green : T.border};
  color: ${props => props.$isCorrect ? T.white : T.textSecondary};
`

const OptionText = styled.span`
  flex: 1;
  padding-top: 1px;
`

const CorrectIcon = styled(CheckCircleFilled)`
  color: ${T.green};
  font-size: 14px;
  margin-left: auto;
  flex-shrink: 0;
  padding-top: 3px;
`

const CodingMetaBlock = styled.div`
  margin-top: 14px;
  background: #1E293B;
  border-radius: ${T.radius};
  padding: 14px 18px;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  color: #E2E8F0;
  line-height: 1.7;
  overflow-x: auto;
`

const CodingLabel = styled.span`
  color: #94A3B8;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.8px;
  display: block;
  margin-bottom: 2px;
  margin-top: ${props => props.$first ? '0' : '10px'};
`

const CodingValue = styled.div`
  color: #F1F5F9;
  white-space: pre-wrap;
  word-break: break-word;
`

const LanguageTag = styled.span`
  display: inline-block;
  background: #334155;
  color: #38BDF8;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${T.textSecondary};
  text-align: center;

  .anticon {
    font-size: 40px;
    color: ${T.border};
    margin-bottom: 16px;
  }
`

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${T.textPrimary};
  margin-bottom: 4px;
`

const EmptyDesc = styled.div`
  font-size: 13px;
  color: ${T.textSecondary};
`

/* ──────────── Helpers ──────────── */

const getDifficultyStyle = (level) => {
  if (!level) return null
  const l = String(level).toLowerCase()
  if (l === 'easy' || l === 'low') return { color: '#065F46', bg: T.green80, label: 'Easy' }
  if (l === 'medium' || l === 'moderate') return { color: '#92400E', bg: '#FFF3E0', label: 'Medium' }
  if (l === 'hard' || l === 'high' || l === 'difficult') return { color: '#991B1B', bg: '#FEE2E2', label: 'Hard' }
  return { color: T.textSecondary, bg: T.grey92, label: level }
}

const splitScenario = (text) => {
  if (!text) return { scenario: null, question: text }
  // Look for common scenario patterns
  const markers = ['Question:', 'Q:', '\nAsk:', '\n\n']
  for (const marker of markers) {
    const idx = text.indexOf(marker)
    if (idx > 60 && idx < text.length - 20) {
      return {
        scenario: text.slice(0, idx).trim(),
        question: text.slice(idx + (marker === '\n\n' ? 2 : marker.length)).trim(),
      }
    }
  }
  return { scenario: null, question: text }
}

/* ──────────── Categorize ──────────── */

const categorizeQuestions = (set) => {
  const sections = {
    mcq: { title: 'MCQ Questions', questions: [] },
    subjective: { title: 'Subjective Questions', questions: [] },
    video: { title: 'Video Response', questions: [] },
    coding: { title: 'Coding Questions', questions: [] },
  }

  if (!set?.questionMaps) return sections

  for (const qm of set.questionMaps) {
    const question = qm.question
    if (!question) continue

    const sectionName = question.sectionMaps?.[0]?.section?.sectionName?.toLowerCase() || ''

    const entry = {
      id: question.id,
      questionMapId: qm.id,
      text: question.questionText,
      options: question.options || [],
      explanation: question.explanation,
      difficulty: question.difficulty,
      metadata: question.metadata,
      codingMetadata: question.codingMetadata,
    }

    if (sectionName.includes('mcq')) {
      sections.mcq.questions.push(entry)
    } else if (sectionName.includes('subjective')) {
      sections.subjective.questions.push(entry)
    } else if (sectionName.includes('video')) {
      sections.video.questions.push(entry)
    } else if (sectionName.includes('coding')) {
      sections.coding.questions.push(entry)
    } else {
      // Default to MCQ
      sections.mcq.questions.push(entry)
    }
  }

  return sections
}

/* ──────────── Component ──────────── */

const QuestionPreview = ({ set, onRegenerateQuestion, regeneratingQuestions, isReadOnly }) => {
  const sections = categorizeQuestions(set)

  const renderQuestion = (q, index, sectionType) => {
    const isRegenerating = regeneratingQuestions.has(q.id)
    const meta = SECTION_META[sectionType] || SECTION_META.mcq
    const prefix = sectionType === 'video' ? 'V' : sectionType === 'coding' ? 'C' : sectionType === 'subjective' ? 'S' : 'Q'
    const diffStyle = getDifficultyStyle(q.difficulty)
    const lang = q.codingMetadata?.language || q.metadata?.language

    // Subjective scenario split
    const isSubjective = sectionType === 'subjective'
    const { scenario, question: questionText } = isSubjective ? splitScenario(q.text) : { scenario: null, question: q.text }

    return (
      <QuestionCard key={q.id} $accent={meta.color}>
        <QHeader>
          <QHeaderLeft>
            <QNumBadge $color={meta.color}>{prefix}{index + 1}</QNumBadge>
            {diffStyle && (
              <DifficultyBadge $color={diffStyle.color} $bg={diffStyle.bg}>
                {diffStyle.label}
              </DifficultyBadge>
            )}
            {sectionType === 'video' && (
              <VideoIndicator>
                <VideoCameraOutlined /> Video
              </VideoIndicator>
            )}
          </QHeaderLeft>
          {!isReadOnly && (
            <RegenerateBtn
              $loading={isRegenerating}
              disabled={isRegenerating}
              onClick={() => onRegenerateQuestion(q.id)}
            >
              <ReloadOutlined />
              {isRegenerating ? 'Regenerating...' : 'Regenerate'}
            </RegenerateBtn>
          )}
        </QHeader>

        {/* Subjective: scenario callout + question */}
        {isSubjective && scenario ? (
          <>
            <ScenarioCallout>{scenario}</ScenarioCallout>
            <QText>{questionText}</QText>
          </>
        ) : (
          <QText>{questionText || q.text}</QText>
        )}

        {/* MCQ Options */}
        {q.options.length > 0 && (
          <OptionsGrid>
            {q.options.map((opt, i) => {
              const isCorrect = opt.optionValue === 1
              return (
                <OptionItem
                  key={opt.id || i}
                  $isCorrect={isCorrect}
                >
                  <OptionLetter $isCorrect={isCorrect}>
                    {String.fromCharCode(65 + i)}
                  </OptionLetter>
                  <OptionText>{opt.optionText}</OptionText>
                  {isCorrect && <CorrectIcon />}
                </OptionItem>
              )
            })}
          </OptionsGrid>
        )}

        {/* Coding metadata */}
        {sectionType === 'coding' && q.codingMetadata && (
          <CodingMetaBlock>
            {lang && <LanguageTag>{lang}</LanguageTag>}
            {q.codingMetadata.input_format && (
              <>
                <CodingLabel $first={!lang}>Input Format</CodingLabel>
                <CodingValue>{q.codingMetadata.input_format}</CodingValue>
              </>
            )}
            {q.codingMetadata.output_format && (
              <>
                <CodingLabel>Output Format</CodingLabel>
                <CodingValue>{q.codingMetadata.output_format}</CodingValue>
              </>
            )}
            {q.codingMetadata.constraints && (
              <>
                <CodingLabel>Constraints</CodingLabel>
                <CodingValue>{q.codingMetadata.constraints}</CodingValue>
              </>
            )}
            {q.codingMetadata.sample_input && (
              <>
                <CodingLabel>Sample Input</CodingLabel>
                <CodingValue>{q.codingMetadata.sample_input}</CodingValue>
              </>
            )}
            {q.codingMetadata.sample_output && (
              <>
                <CodingLabel>Sample Output</CodingLabel>
                <CodingValue>{q.codingMetadata.sample_output}</CodingValue>
              </>
            )}
          </CodingMetaBlock>
        )}
      </QuestionCard>
    )
  }

  const isEmpty = Object.values(sections).every(s => s.questions.length === 0)

  return (
    <Wrapper>
      {Object.entries(sections).map(([key, section]) => {
        if (section.questions.length === 0) return null
        const meta = SECTION_META[key] || SECTION_META.mcq
        return (
          <SectionBlock key={key}>
            <SectionHeader $accentColor={meta.color}>
              <SectionIconWrap $color={meta.color}>
                {meta.icon}
              </SectionIconWrap>
              <SectionTitle>{section.title}</SectionTitle>
              <SectionCount $color={meta.color}>
                {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
              </SectionCount>
            </SectionHeader>
            {section.questions.map((q, i) => renderQuestion(q, i, key))}
          </SectionBlock>
        )
      })}

      {isEmpty && (
        <EmptyState>
          <InboxOutlined />
          <EmptyTitle>No questions yet</EmptyTitle>
          <EmptyDesc>Questions will appear here once they are generated for this set.</EmptyDesc>
        </EmptyState>
      )}
    </Wrapper>
  )
}

export default QuestionPreview
